import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { HeaderComponent, BodyComponent, InputText, SaveButton, UploadFile, SkeletonComponent, ErrorComponent } from '@comps/components';
import { Form, Input } from "antd";
import toast from "@comps/toast";
import { Icon } from "@iconify/react";
import { getErrorProps, makeRequest } from "@utils/helpers";
import { getSettings, updateSettings } from "./requests";
import { settingsUpdated } from "@mods/userManagement/auth/authSlice";
import DefaultLogo from "@assets/images/logo.png";
import { MEDIA_FORMATS } from "@utils/mediaFormats";
import "./settings.css";

const pageConfig = {
  headers: {
    title: "Settings",
    breadcrumb: [{ name: "Settings", path: "/settings" }]
  }
};

/*
 * The same screen serves two very different jobs, and the backend says
 * which: `scope: "own"` means a doctor is editing their own white-label
 * brand — the name and logo only they will see once they sign in — while
 * "platform" means an admin is editing what everyone else sees. The
 * contact number and footer text describe the platform itself, so they
 * are not a doctor's to set and the backend ignores them from a doctor
 * regardless; hiding the section here keeps the screen honest about it.
 */
const isOwnBrand = (data) => data && data.scope === "own";

const formName = "settings_form";

/** Titled band with helper text — one per group of related fields. */
const Section = ({ icon, title, description, children }) => (
  <section className="settings-section">
    <div className="settings-section-head">
      <span className="settings-section-mark">
        <Icon icon={ icon } width={ 20 } />
      </span>
      <div>
        <h2 className="settings-section-title">{ title }</h2>
        <p className="settings-section-desc">{ description }</p>
      </div>
    </div>
    <div className="settings-section-body">{ children }</div>
  </section>
);

const Settings = () => {
  const dispatch = useDispatch();
  // Loading the settings and saving them are separate waits. One shared
  // flag meant the Save button wore "Working…" during the opening fetch,
  // and the page itself had nothing to show at all until it landed — a
  // blank card under a heading, then everything at once.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [errors, setErrors] = useState([]);
  const [logo, setLogo] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [failed, setFailed] = useState(false);

  const load = () => {
    setFailed(false);
    makeRequest(setLoading, getSettings, null, setInitialData, onLoadError);
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A failed load left the card empty behind a toast that had already
  // faded — say so on the page, where it can be acted on.
  const onLoadError = () => setFailed(true);

  const onError = () => toast.error("Settings didn't load. Refresh to try again.");

  const onFinish = (values) => {
    const payload = { ...values, id: initialData.id, file: logo };
    makeRequest(setSaving, updateSettings, payload, onUpdateSuccess, onError);
  };

  const onUpdateSuccess = (res) => {
    // The sidebar, footer and browser tab all read the auth slice, not
    // localStorage — writing only to localStorage (as this did) left them
    // on the old name and logo until someone reloaded the page. The
    // reducer keeps localStorage in step for the next cold start.
    dispatch(settingsUpdated(res));
    setInitialData(res);
    setDirty(false);
    toast.success(
      "Settings saved",
      isOwnBrand(res)
        ? `Your workspace is now called "${res.title}".`
        : `The app is now called "${res.title}".`
    );
  };

  const touched = () => { setErrors([]); setDirty(true); };

  const ownBrand = isOwnBrand(initialData);

  return (
    <>
      <HeaderComponent
        headers={ pageConfig.headers }
        description={ ownBrand
          ? "Your own name and logo. Nobody else sees this — it replaces the default branding everywhere you sign in."
          : "How the app names and presents itself to everyone who signs in." }
      />

      <BodyComponent narrow>
        {/* The skeleton stands in for the sections that are coming, so the
            page has its shape from the first frame and nothing jumps when
            the settings arrive. */}
        { loading && <SkeletonComponent variant="sections" rows={ 3 } /> }

        { !loading && failed &&
          <ErrorComponent
            title="Settings didn't load"
            description="The server didn't answer. Check your connection, then try again."
            onRetry={ load }
          /> }

        { !loading && !failed && initialData && initialData.title &&
          <Form
            layout="vertical"
            name={ formName }
            onFinish={ onFinish }
            initialValues={ initialData }
            onValuesChange={ touched }
            className="settings-form"
          >
            <Section
              icon="tabler:id-badge"
              title="Identity"
              description={ ownBrand
                ? "The name shown in your sidebar and browser tab."
                : "The name and description shown across the app." }
            >
              <Form.Item name="title" rules={ rules.title } label="Title"
                { ...getErrorProps(errors['title']) }>
                <InputText maxLength={ 100 } placeholder="SmileCorrect" />
              </Form.Item>

              <Form.Item name="description" rules={ rules.description } label="Description"
                { ...getErrorProps(errors['description']) }>
                <Input.TextArea rows={ 3 } maxLength={ TEXT_LIMIT } showCount
                  placeholder="What this workspace is for" />
              </Form.Item>
            </Section>

            { !ownBrand &&
              <Section
                icon="tabler:phone"
                title="Contact"
                description="Where people should reach you, and what sits in the page footer."
              >
                <Form.Item name="contact_number" rules={ rules.contact_number } label="Contact number"
                  { ...getErrorProps(errors['contact_number']) }>
                  <InputText maxLength={ TEXT_LIMIT } placeholder="+92 300 0000000" />
                </Form.Item>

                <Form.Item name="footer_text" rules={ rules.footer_text } label="Footer text"
                  { ...getErrorProps(errors['footer_text']) }>
                  <Input.TextArea rows={ 2 } maxLength={ TEXT_LIMIT } showCount
                    placeholder="All rights reserved." />
                </Form.Item>
              </Section> }

            <Section
              icon="tabler:photo"
              title="Branding"
              description={ ownBrand
                ? "The logo shown in your sidebar in place of the default one."
                : "The mark shown in the sidebar and on the sign-in screen." }
            >
              <UploadFile
                label="Logo"
                text="Choose a logo"
                accept={ MEDIA_FORMATS.image.accept }
                extensions={ MEDIA_FORMATS.image.extensions }
                formatLabel={ MEDIA_FORMATS.image.label }
                onUpload={ (file) => { setLogo(file); setDirty(true); } }
              />

              <div className="logo-preview">
                <span className="logo-preview-label">Sidebar preview</span>
                <div className="logo-preview-rail">
                  { initialData.logo
                    ? <>
                        <span className="logo-preview-mark">
                          <img src={ process.env.REACT_APP_BASE_URL + initialData.logo } alt="" />
                        </span>
                        <span className="logo-preview-text">
                          <strong>{ initialData.title || "SmileCorrect" }</strong>
                          <span>Case management</span>
                        </span>
                      </>
                    : <img className="logo-preview-logo" src={ DefaultLogo } alt="The Smile Correct" /> }
                </div>
              </div>
            </Section>

            { dirty &&
              <div className="settings-savebar">
                <span>You have unsaved changes.</span>
                <SaveButton form={ formName } htmlType="submit" state={ saving } />
              </div> }
          </Form> }
      </BodyComponent>
    </>
  );
};

/*
 * Every text column on the settings table is varchar(255). Only `title`
 * was ever checked, so a longer description, contact number or footer
 * reached the database as an over-length UPDATE and came back as an
 * Internal Server Error. The server rejects these properly now too —
 * this is so the reader is told before they press Save.
 */
const TEXT_LIMIT = 255;

const lengthRule = [{ max: TEXT_LIMIT, message: `Keep this to ${TEXT_LIMIT} characters or fewer` }];

const rules = {
  title: [
    { required: true, message: 'Enter a title' },
    { min: 3, message: 'Use at least 3 characters' },
    { max: 100, message: 'Keep it under 100 characters' },
  ],
  description: lengthRule,
  contact_number: lengthRule,
  footer_text: lengthRule,
};

export default Settings;
